import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Tag, Space, message, Tooltip, Dropdown, Modal, Input, Select, DatePicker, Radio } from 'antd';
import type { MenuProps } from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, CopyOutlined, CheckCircleOutlined, SearchOutlined, ReloadOutlined, CalendarOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/Layout/MainLayout';
import { draftService } from '../../services/draftService';
import { Draft } from '../../types/draft';
import dayjs from 'dayjs';

const DraftList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<Draft[]>([]);
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [selectedRecord, setSelectedRecord] = useState<Draft | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editReason, setEditReason] = useState('');

  // 검색 관련 상태
  const [searchModalVisible, setSearchModalVisible] = useState(false);
  const [searchType, setSearchType] = useState('제목');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [paymentFilter, setPaymentFilter] = useState<'all' | 'paid' | 'unpaid' | 'pending'>('all');

  // 기안서 목록 조회 (검색 조건 포함)
  const fetchDraftList = async (params?: {
    searchText?: string;
    startDate?: string;
    endDate?: string;
    payYN?: string;
  }) => {
    setLoading(true);
    try {
      // 쿼리 파라미터 구성
      let queryString = '';
      if (params) {
        const queryParams = new URLSearchParams();
        if (params.searchText) queryParams.append('searchText', params.searchText);
        if (params.startDate) queryParams.append('startDate', params.startDate);
        if (params.endDate) queryParams.append('endDate', params.endDate);
        if (params.payYN) queryParams.append('payYN', params.payYN);
        queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      }

      const response = await draftService.getDraftList(queryString);
      if (response.resultCode === 200) {
        setDataSource(response.data);
      } else {
        message.error(response.message || '기안서 목록 조회 실패');
      }
    } catch (error) {
      console.error('기안서 목록 조회 오류:', error);
      message.error('기안서 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDraftList();
  }, []);

  // 검색 실행
  const handleSearch = () => {
    const params: any = {};

    // 제목 검색
    if (searchKeyword.trim()) {
      params.searchText = searchKeyword.trim();
    }

    // 기간 검색
    if (startDate && endDate) {
      params.startDate = startDate;
      params.endDate = endDate;
    }

    // 비용처리 상태
    if (paymentFilter === 'paid') {
      params.payYN = 'Y';
    } else if (paymentFilter === 'unpaid') {
      params.payYN = 'N';
    }

    fetchDraftList(params);
  };

  // 검색 초기화
  const handleReset = () => {
    setSearchKeyword('');
    setStartDate('');
    setEndDate('');
    setPaymentFilter('all');
    fetchDraftList();
  };

  // 테이블 컬럼 정의
  const columns: ColumnsType<Draft> = [
    {
      title: 'No',
      dataIndex: 'Draft_IDX',
      key: 'Draft_IDX',
      width: 80,
      align: 'center',
    },
    {
      title: '기안번호',
      key: 'Draft_No',
      width: 120,
      align: 'center',
      render: (_, record) => `${record.Draft_Year}-${record.Draft_No}`,
    },
    {
      title: '수정',
      dataIndex: 'Edit_YN',
      key: 'Edit_YN',
      width: 80,
      align: 'center',
      render: (value) => (
        <Tag color={value === 'Y' || value === 1 ? 'warning' : 'default'}>
          {value === 'Y' || value === 1 ? '수정' : '미수정'}
        </Tag>
      ),
    },
    {
      title: '제목',
      dataIndex: 'Draft_Subject',
      key: 'Draft_Subject',
      ellipsis: true,
    },
    {
      title: '거래처',
      dataIndex: 'Draft_Client',
      key: 'Draft_Client',
      width: 150,
    },
    {
      title: '금액',
      dataIndex: 'Draft_Cost',
      key: 'Draft_Cost',
      width: 120,
      align: 'right',
      render: (value) => value ? value.toLocaleString() + '원' : '-',
    },
    {
      title: '등록일',
      dataIndex: 'Reg_Date',
      key: 'Reg_Date',
      width: 120,
      align: 'center',
      render: (value) => value ? dayjs(value).format('YYYY-MM-DD') : '-',
    },
    {
      title: '승인',
      dataIndex: 'Confirm_YN',
      key: 'Confirm_YN',
      width: 80,
      align: 'center',
      render: (value) => (
        <Tag color={value === 'Y' || value === 1 ? 'success' : 'default'}>
          {value === 'Y' || value === 1 ? '승인' : '대기'}
        </Tag>
      ),
    },
    {
      title: '결재',
      dataIndex: 'Pay_YN',
      key: 'Pay_YN',
      width: 80,
      align: 'center',
      render: (value) => (
        <Tag color={value === 'Y' || value === 1 ? 'success' : 'default'}>
          {value === 'Y' || value === 1 ? '완료' : '대기'}
        </Tag>
      ),
    },
  ];

  // 컨텍스트 메뉴 항목
  const getContextMenuItems = (record: Draft): MenuProps['items'] => [
    {
      key: 'view',
      label: '상세 보기',
      icon: <EyeOutlined />,
      onClick: () => {
        setContextMenuVisible(false);
        navigate(`/draft/view/${record.Draft_IDX}`);
      },
    },
    {
      key: 'edit',
      label: '수정',
      icon: <EditOutlined />,
      disabled: record.Edit_YN === 'N' || record.Edit_YN === 0,
      onClick: () => {
        setContextMenuVisible(false);
        navigate(`/draft/modify/${record.Draft_IDX}`);
      },
    },
    {
      key: 'copy',
      label: '복사',
      icon: <CopyOutlined />,
      onClick: () => {
        setContextMenuVisible(false);
        navigate(`/draft/copy/${record.Draft_IDX}`);
      },
    },
  ];

  // 행 클릭 핸들러
  const handleRowClick = (record: Draft, event: React.MouseEvent) => {
    setSelectedRecord(record);
    setContextMenuVisible(true);
    setContextMenuPosition({ x: event.clientX, y: event.clientY });
  };

  // 행 더블클릭 핸들러
  const handleRowDoubleClick = (record: Draft) => {
    navigate(`/draft/view/${record.Draft_IDX}`);
  };

  // 수정 사유 입력 후 수정 페이지 이동
  const handleEditConfirm = () => {
    if (!editReason.trim()) {
      message.warning('수정 사유를 입력해주세요.');
      return;
    }

    if (selectedRecord) {
      navigate(`/draft/modify/${selectedRecord.Draft_IDX}`, {
        state: { editReason }
      });
    }
    setEditModalVisible(false);
    setEditReason('');
  };

  // 비용처리 완료
  const handlePaymentComplete = async (record: Draft) => {
    Modal.confirm({
      title: '비용처리 완료',
      content: `${record.Draft_No} - ${record.Draft_Subject} 기안서의 비용처리를 완료하시겠습니까?`,
      okText: '확인',
      cancelText: '취소',
      onOk: async () => {
        try {
          // TODO: 비용처리 완료 API 호출
          message.success('비용처리가 완료되었습니다.');
          fetchDraftList(); // 목록 새로고침
        } catch (error) {
          console.error('비용처리 오류:', error);
          message.error('비용처리 중 오류가 발생했습니다.');
        }
      },
    });
  };

  // 외부 클릭 시 메뉴 닫기
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenuVisible) {
        setContextMenuVisible(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [contextMenuVisible]);

  return (
    <MainLayout>
      {/* 수정 사유 입력 모달 */}
      <Modal
        title="수정 사유 입력"
        open={editModalVisible}
        onOk={handleEditConfirm}
        onCancel={() => {
          setEditModalVisible(false);
          setEditReason('');
        }}
        okText="수정하기"
        cancelText="취소"
      >
        <div style={{ marginBottom: 8 }}>
          수정 사유를 입력해주세요. (히스토리에 기록됩니다)
        </div>
        <Input.TextArea
          value={editReason}
          onChange={(e) => setEditReason(e.target.value)}
          placeholder="수정 사유를 입력하세요"
          rows={4}
        />
      </Modal>

      {/* 기간 검색 모달 */}
      <Modal
        title="기간 검색"
        open={searchModalVisible}
        onOk={() => {
          setSearchModalVisible(false);
          handleSearch();
        }}
        onCancel={() => setSearchModalVisible(false)}
        width={500}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* 빠른 선택 버튼 - 동적 월 표시 */}
          <div>
            <Space wrap>
              <Button onClick={() => {
                const today = dayjs();
                setStartDate(today.format('YYYY-MM-DD'));
                setEndDate(today.format('YYYY-MM-DD'));
              }}>
                오늘
              </Button>
              {[0, 1, 2, 3].map((monthsAgo) => {
                const targetMonth = dayjs().subtract(monthsAgo, 'month');
                const monthName = targetMonth.format('M월');

                return (
                  <Button
                    key={monthsAgo}
                    onClick={() => {
                      setStartDate(targetMonth.startOf('month').format('YYYY-MM-DD'));
                      setEndDate(targetMonth.endOf('month').format('YYYY-MM-DD'));
                    }}
                  >
                    {monthName}
                  </Button>
                );
              })}
              <Button onClick={() => {
                setStartDate('');
                setEndDate('');
              }}>
                전체
              </Button>
            </Space>
          </div>

          {/* 날짜 범위 */}
          <div>
            <label style={{ display: 'block', marginBottom: 8 }}>등록일자</label>
            <Space>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ width: 150 }}
              />
              <span>~</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ width: 150 }}
              />
            </Space>
          </div>

          {/* 비용처리 필터 */}
          <div>
            <label style={{ display: 'block', marginBottom: 8 }}>비용처리</label>
            <Radio.Group value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}>
              <Radio value="all">전체</Radio>
              <Radio value="pending">처리전</Radio>
              <Radio value="paid">처리완료</Radio>
            </Radio.Group>
          </div>
        </Space>
      </Modal>

      <Card
        title={
          <Space>
            <span>기안서 리스트</span>
            {/* 제목 검색 */}
            <Select value={searchType} onChange={setSearchType} style={{ width: 100 }}>
              <Select.Option value="제목">제목</Select.Option>
              <Select.Option value="거래처">거래처</Select.Option>
            </Select>
            <Input
              placeholder="검색어 입력"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              style={{ width: 200 }}
              onPressEnter={handleSearch}
            />
            <Button icon={<SearchOutlined />} onClick={handleSearch}>
              검색
            </Button>
          </Space>
        }
        extra={
          <Space>
            <Button icon={<CalendarOutlined />} onClick={() => setSearchModalVisible(true)}>
              기간 검색
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              새로고침
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/draft/regist')}
            >
              기안서 등록
            </Button>
          </Space>
        }
      >
        <div style={{ position: 'relative' }}>
          <Tooltip title="클릭: 메뉴 열기 | 더블클릭: 상세보기" placement="topLeft">
            <Table
              columns={columns}
              dataSource={dataSource}
              rowKey="Draft_IDX"
              loading={loading}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `전체 ${total}건`,
              }}
              scroll={{ x: 1200 }}
              onRow={(record) => ({
                onClick: (e) => {
                  e.stopPropagation();
                  handleRowClick(record, e);
                },
                onDoubleClick: () => handleRowDoubleClick(record),
                style: { cursor: 'pointer' },
              })}
            />
          </Tooltip>

          {/* 컨텍스트 메뉴 */}
          {contextMenuVisible && selectedRecord && (
            <div
              style={{
                position: 'fixed',
                left: contextMenuPosition.x,
                top: contextMenuPosition.y,
                zIndex: 1000,
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08)',
                padding: '4px',
                minWidth: '150px',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                onClick={() => {
                  setContextMenuVisible(false);
                  navigate(`/draft/view/${selectedRecord.Draft_IDX}`);
                }}
              >
                <EyeOutlined /> 상세 보기
              </div>
              <div
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                onClick={() => {
                  setContextMenuVisible(false);
                  setEditModalVisible(true);
                }}
              >
                <EditOutlined /> 수정
              </div>
              <div
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                onClick={() => {
                  setContextMenuVisible(false);
                  navigate(`/draft/copy/${selectedRecord.Draft_IDX}`);
                }}
              >
                <CopyOutlined /> 복사
              </div>
              <div style={{ height: '1px', background: '#f0f0f0', margin: '4px 0' }} />
              <div
                style={{
                  padding: '8px 12px',
                  cursor: selectedRecord.Pay_YN === 'Y' || selectedRecord.Pay_YN === 1 ? 'not-allowed' : 'pointer',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: selectedRecord.Pay_YN === 'Y' || selectedRecord.Pay_YN === 1 ? '#999' : '#52c41a',
                  opacity: selectedRecord.Pay_YN === 'Y' || selectedRecord.Pay_YN === 1 ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!(selectedRecord.Pay_YN === 'Y' || selectedRecord.Pay_YN === 1)) {
                    e.currentTarget.style.background = '#f6ffed';
                  }
                }}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                onClick={() => {
                  if (!(selectedRecord.Pay_YN === 'Y' || selectedRecord.Pay_YN === 1)) {
                    setContextMenuVisible(false);
                    handlePaymentComplete(selectedRecord);
                  }
                }}
              >
                <CheckCircleOutlined /> 비용처리 완료
              </div>
            </div>
          )}
        </div>
      </Card>
    </MainLayout>
  );
};

export default DraftList;
